import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, catchError, firstValueFrom, Observable, of} from 'rxjs';
import {TranslocoService} from '@jsverse/transloco';
import {setDefaultOptions} from 'date-fns';
import type {Locale} from 'date-fns';
import {it, enGB} from 'date-fns/locale';

import {StorageProperties} from './local-storage.service';

export type AppLang = 'it' | 'en';

const DEFAULT_LANG: AppLang = 'it';
const SUPPORTED_LANGS: AppLang[] = ['it', 'en'];

const DATE_FNS_LOCALE: Record<AppLang, Locale> = {
  it,
  en: enGB,
};

/**
 * Punto unico di cambio lingua dell'app (UI statica).
 * use(lang) applica, in ordine, tutti gli effetti collaterali del cambio lingua:
 *   1. lingua attiva Transloco
 *   2. attributo <html lang> (AgID/WCAG 2.1 AA, 3.1.1)
 *   3. locale di default di date-fns (centralizzato qui)
 *   4. persistenza locale (StorageProperties.LANG)
 *
 * Modellato su TextSizeService: stessa sorgente di persistenza (localStorage) e
 * stessa logica di applicazione su documentElement.
 */
@Injectable({providedIn: 'root'})
export class LanguageService {

  private transloco = inject(TranslocoService);

  readonly availableLangs: AppLang[] = SUPPORTED_LANGS;
  readonly activeLang$ = new BehaviorSubject<AppLang>(DEFAULT_LANG);

  /**
   * Risolve la lingua iniziale, la applica e attende il caricamento del dizionario
   * root (scope `common`) nello store Transloco prima di risolvere. Così le chiavi
   * `common.*` risolvono già al primo render della prima pagina, evitando la race
   * che le mostrava grezze (la pipe `| transloco` è sincrona e non si auto-corregge).
   * Da chiamare al boot, prima del primo render. Un errore di rete sul load NON
   * blocca il boot: si prosegue in modalità degradata (caricamento lazy).
   */
  async init(): Promise<void> {
    const target = this.resolveInitialLang();
    this.use(target);
    await firstValueFrom(
      this.transloco.load(target).pipe(
        catchError((err) => {
          console.warn('[LanguageService] root dictionary load failed, continuing', err);
          return of(undefined);
        }),
      ),
    );
  }

  /** Cambia la lingua attiva applicando tutti gli effetti collaterali. */
  use(lang: AppLang): void {
    const target = this.isSupported(lang) ? lang : DEFAULT_LANG;

    this.transloco.setActiveLang(target);
    document.documentElement.lang = target;
    setDefaultOptions({locale: DATE_FNS_LOCALE[target]});
    localStorage.setItem(StorageProperties.LANG, target);

    this.activeLang$.next(target);
  }

  getActiveLang(): AppLang {
    return this.activeLang$.value;
  }

  watchActiveLang(): Observable<AppLang> {
    return this.activeLang$.asObservable();
  }

  /** Priorità: lingua salvata → lingua del browser (se supportata) → it. */
  private resolveInitialLang(): AppLang {
    const saved = localStorage.getItem(StorageProperties.LANG);
    if (this.isSupported(saved)) {
      return saved;
    }

    const browser = (navigator.language || '').slice(0, 2).toLowerCase();
    if (this.isSupported(browser)) {
      return browser;
    }

    return DEFAULT_LANG;
  }

  private isSupported(value: string | null): value is AppLang {
    return value === 'it' || value === 'en';
  }
}
